import { Authenticator } from "@aws-amplify/ui-react";
import {
  QueryClient,
  // QueryClientProvider
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import * as React from "react";
import { ErrorBoundary } from "react-error-boundary";
import { HelmetProvider } from "react-helmet-async";

import { Spinner } from "@/components/atoms/Spinner/Spinner";
import { TANSTACK_QUERY_PERSIST_MAX_AGE_MS } from "@/config/tanstack-query";
import { PersistResetContext } from "@/hooks/use-persist-reset-context";
import { createIDBPersister } from "@/lib/tanstack-query-persist";

type AppProviderProps = {
  children: React.ReactNode;
};

const GlobalErrorFallback = () => (
  <div
    className="grid h-screen place-content-center place-items-center gap-4 bg-white"
    role="alert"
  >
    <h1 className="text-XXLarge font-bold text-gray-600">
      ⚠️ エラーが発生しました
    </h1>
    <p className="text-gray-500">ページを更新して再度お試しください。</p>
  </div>
);

const persister = createIDBPersister();

export const AppProvider = ({ children }: AppProviderProps) => {
  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
          },
        },
      })
  );
  const [hasRestored, setHasRestored] = React.useState(false);

  return (
    <React.Suspense
      fallback={
        <div className="fixed inset-0 grid place-content-center bg-white">
          <Spinner className="size-5 text-primary" />
        </div>
      }
    >
      <ErrorBoundary fallback={<GlobalErrorFallback />}>
        <Authenticator.Provider>
          <PersistQueryClientProvider
            client={queryClient}
            persistOptions={{
              persister: persister,
              maxAge: TANSTACK_QUERY_PERSIST_MAX_AGE_MS,
              dehydrateOptions: {
                shouldDehydrateQuery: (query) =>
                  Boolean(query.meta?.persist === true),
              },
            }}
            onSuccess={() => {
              setHasRestored(true);
            }}
            onError={() => {
              setHasRestored(true);
            }}
          >
            <PersistResetContext value={persister.removeClient}>
              <ReactQueryDevtools initialIsOpen={false} />
              <HelmetProvider>
                {hasRestored ? (
                  children
                ) : (
                  <div className="grid h-screen place-content-center bg-white">
                    <Spinner className="size-5 text-primary" />
                  </div>
                )}
              </HelmetProvider>
            </PersistResetContext>
          </PersistQueryClientProvider>
        </Authenticator.Provider>
      </ErrorBoundary>
    </React.Suspense>
  );
};
